/**
 * StorageShelf
 *
 * Collapsible card that renders the full grid of positions for one shelf
 * (balda) of a storage. Supports column pagination when the shelf has more
 * than COLS_PER_PAGE columns (e.g. ATC with 360 columns).
 *
 * Props:
 *   shelveNum       – Shelf number (integer, 1-based from backend)
 *   dims            – { rows: Set<number>, cols: Set<number> } — unique row/col indices
 *   shelveMap       – { [row]: { [col]: positionObject } } — lookup for fast cell render
 *   positions       – position[] for this shelf only (used for occupancy count)
 *   pendingChanges  – { [posId]: pendingChange } for the current storage (from currentPendingChanges)
 *   isExpanded      – controlled collapse state
 *   onToggle        – () => void
 *   selectedResource – resource being placed (passed to cells for isSelecting visual)
 *   onCellClick     – (event, pos) => void — forwarded to StoragePositionCell
 *
 * Column pagination:
 *   COLS_PER_PAGE = 20. State `colPage` is local to each shelf instance.
 *   Navigation: |◀ ◀ C1–C20 (1/18) ▶ ▶|
 */
import React, { useState } from 'react'
import { Box, Typography, Chip, Stack, Collapse, IconButton, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { StorageRounded, ExpandMoreRounded, ExpandLessRounded, ChevronLeftRounded, ChevronRightRounded, FirstPageRounded, LastPageRounded } from '@mui/icons-material'
import { styles, shelfHeaderSx } from './warehouseStorageStyles'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import StoragePositionCell from './StoragePositionCell'

const COLS_PER_PAGE = 20

export default function StorageShelf ({ shelveNum, dims, shelveMap, positions, pendingChanges, isExpanded, onToggle, selectedResource, isSelecting, onCellClick, getCompatibility }) {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const accent = theme.palette.primary.main

  const [colPage, setColPage] = useState(0)

  if (!dims) return null

  const sortedRows = [...dims.rows].sort((a, b) => b - a)
  const sortedCols = [...dims.cols].sort((a, b) => a - b)
  const totalColPages = Math.max(1, Math.ceil(sortedCols.length / COLS_PER_PAGE))
  const safePage = Math.min(colPage, totalColPages - 1)
  const visibleCols = sortedCols.slice(safePage * COLS_PER_PAGE, (safePage + 1) * COLS_PER_PAGE)
  const firstCol = visibleCols[0]
  const lastCol = visibleCols[visibleCols.length - 1]

  const occupiedCount = positions.filter(p => {
    const pending = pendingChanges[p.id]
    if (pending) return pending.action === 'set'
    return p.resources && p.resources.length > 0
  }).length

  // Count compatible empty positions when a resource is selected
  const compatibleCount = getCompatibility
    ? positions.filter(p => getCompatibility(p) === 'compatible').length
    : null

  return (
    <Box sx={{ mb: 1.5, overflow: 'hidden', ...styles.shelfCard }}>
      <Box onClick={onToggle} sx={shelfHeaderSx(isExpanded, accent)}>
        <Stack direction='row' alignItems='center' spacing={1.5}>
          <StorageRounded sx={{ color: accent, fontSize: 22 }} />
          <Typography sx={{ fontWeight: 600, fontSize: '1rem', color: '#e0e0e0' }}>
            {t_('Shelf')} {shelveNum}
          </Typography>
          <Chip
            label={`${occupiedCount} / ${positions.length}`}
            size='small'
            sx={{
              bgcolor: occupiedCount > 0 ? 'rgba(76,175,80,0.12)' : 'rgba(255,255,255,0.05)',
              color: occupiedCount > 0 ? '#4caf50' : '#666',
              border: '1px solid',
              borderColor: occupiedCount > 0 ? 'rgba(76,175,80,0.3)' : 'transparent',
              fontSize: '0.72rem',
              height: 24
            }}
          />
          {compatibleCount !== null && compatibleCount > 0 && (
            <Chip
              label={`✓ ${compatibleCount}`}
              size='small'
              sx={{
                bgcolor: compatibleCount > 0 ? 'rgba(0,188,212,0.12)' : 'rgba(255,82,82,0.1)',
                color: compatibleCount > 0 ? '#00bcd4' : '#ff5252',
                border: '1px solid',
                borderColor: compatibleCount > 0 ? 'rgba(0,188,212,0.3)' : 'rgba(255,82,82,0.3)',
                fontSize: '0.72rem',
                height: 24
              }}
            />
          )}
          <Typography sx={styles.axisLabel}>
            {sortedRows.length} {t_('rows')} × {sortedCols.length} {t_('cols')}
          </Typography>
        </Stack>
        <IconButton sx={{ color: accent, minWidth: 48, minHeight: 48 }}>
          {isExpanded ? <ExpandLessRounded sx={{ fontSize: 26 }} /> : <ExpandMoreRounded sx={{ fontSize: 26 }} />}
        </IconButton>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ p: 2 }}>
          {/* Column pagination bar */}
          {totalColPages > 1 && (
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
              px: 0.5
            }}
            >
              <Stack direction='row' spacing={0.5}>
                <IconButton
                  disabled={safePage === 0}
                  onClick={(e) => { e.stopPropagation(); setColPage(0) }}
                  sx={{ color: accent, minWidth: 44, minHeight: 44, opacity: safePage === 0 ? 0.3 : 1 }}
                >
                  <FirstPageRounded sx={{ fontSize: 24 }} />
                </IconButton>
                <IconButton
                  disabled={safePage === 0}
                  onClick={(e) => { e.stopPropagation(); setColPage(safePage - 1) }}
                  sx={{ color: accent, minWidth: 44, minHeight: 44, opacity: safePage === 0 ? 0.3 : 1 }}
                >
                  <ChevronLeftRounded sx={{ fontSize: 28 }} />
                </IconButton>
              </Stack>

              <Stack direction='row' alignItems='center' spacing={1}>
                <Typography sx={{ fontSize: '0.88rem', color: '#bbb', fontWeight: 500 }}>
                  C{firstCol} – C{lastCol}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#777' }}>
                  ({safePage + 1} / {totalColPages})
                </Typography>
              </Stack>

              <Stack direction='row' spacing={0.5}>
                <IconButton
                  disabled={safePage >= totalColPages - 1}
                  onClick={(e) => { e.stopPropagation(); setColPage(safePage + 1) }}
                  sx={{ color: accent, minWidth: 44, minHeight: 44, opacity: safePage >= totalColPages - 1 ? 0.3 : 1 }}
                >
                  <ChevronRightRounded sx={{ fontSize: 28 }} />
                </IconButton>
                <IconButton
                  disabled={safePage >= totalColPages - 1}
                  onClick={(e) => { e.stopPropagation(); setColPage(totalColPages - 1) }}
                  sx={{ color: accent, minWidth: 44, minHeight: 44, opacity: safePage >= totalColPages - 1 ? 0.3 : 1 }}
                >
                  <LastPageRounded sx={{ fontSize: 24 }} />
                </IconButton>
              </Stack>
            </Box>
          )}

          {/* Grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `44px repeat(${visibleCols.length}, 1fr)`,
              gap: 0.75
            }}
          >
            <Box />
            {visibleCols.map(col => (
              <Typography key={col} sx={{ ...styles.axisLabel, textAlign: 'center' }}>C{col}</Typography>
            ))}
            {sortedRows.map(row => (
              <React.Fragment key={row}>
                <Typography sx={{ ...styles.axisLabel, alignSelf: 'center' }}>R{row}</Typography>
                {visibleCols.map(col => {
                  const pos = shelveMap?.[row]?.[col]
                  if (!pos) {
                    return (
                      <Box
                        key={col}
                        sx={{
                          minHeight: 64,
                          bgcolor: 'rgba(0,0,0,0.2)',
                          borderRadius: 1.5,
                          border: `1px dashed ${alpha(accent, 0.08)}`
                        }}
                      />
                    )
                  }
                  return (
                    <Box key={col} sx={{ minWidth: 0 }}>
                      <StoragePositionCell
                        pos={pos}
                        pendingChange={pendingChanges[pos.id]}
                        isSelecting={isSelecting}
                        compatibilityState={getCompatibility ? getCompatibility(pos) : null}
                        onClick={onCellClick}
                      />
                    </Box>
                  )
                })}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Box>
  )
}
